import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { getSentimentDescription } from '../application.utils';
import {
  accomplishedStepsBeforeDiscardedAtom,
  getApplicationStateAtom,
  getApplyJobStatusDataAtom,
  getIsSpontaneousApplicationAtom,
  getListWorkerJobApplicationsHistoryDataAtom,
  getWorkerDiscardedReasonVariableAtom,
} from './application-details.context';
import { useApplicationTimelineStateList } from './application-details.constants';
import { buildApplicationTimeline } from './application-details.timeline';
import { usePreInterview } from './use-pre-interview';

/**
 * The rejection sentence shown in the body of the "rejected" step.
 *
 * This was a derived atom. It is a hook now because it composes user-facing
 * copy, which needs `t`, and because a memoised atom value would keep the old
 * language after a client-side locale switch.
 */
export const useDiscardStatement = () => {
  const { t } = useTranslation();
  const applyJobStatusData = useAtomValue(getApplyJobStatusDataAtom);
  const discardedVariables = useAtomValue(getWorkerDiscardedReasonVariableAtom);

  return useMemo(() => {
    // Explicit precedence, because three different fields can carry the reason
    // and only one of them is populated at a time:
    //   1. the payload's own variable, which is the only source that works when
    //      the manager or the employer rejected the application. The lookup
    //      below is loaded with `parent_key === 'worker'` reasons only, so it
    //      resolves nothing for those two cases and the reason was dropped;
    //   2. the lookup, for a withdrawal the worker made in this session;
    //   3. the free text of the "other reason" flow, which writes no variable
    //      at all.
    // `custom_discard_motivation` is deliberately not read here: it is written
    // by the AI qualification agent and showing it to the worker is a product
    // decision, not a translation one.
    const discardedReason =
      applyJobStatusData.discarded_reason_var?.label ||
      discardedVariables?.find((variable) => variable.id === applyJobStatusData.discarded_reason)?.label ||
      applyJobStatusData.discarded_other_reason ||
      '';

    return getSentimentDescription({
      discardedBy: applyJobStatusData.discarded_by,
      discardedReason,
      t,
    })?.trim();
  }, [
    applyJobStatusData.discarded_by,
    applyJobStatusData.discarded_reason,
    applyJobStatusData.discarded_reason_var?.label,
    applyJobStatusData.discarded_other_reason,
    discardedVariables,
    t,
  ]);
};

/**
 * The application timeline, translated, dated and filtered, ready to render.
 *
 * Replaces `getApplicationTimelineStateListAtom` and the store field it read
 * from. That field was written once, on the first render where the list was
 * empty, and never rewritten, so it froze not only the language but the shape
 * of the timeline: the ATS filter and the rejection heading kept whatever they
 * were computed with the first time.
 */
export const useApplicationTimeline = () => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const base = useApplicationTimelineStateList();
  const isSpontaneousApplication = useAtomValue(getIsSpontaneousApplicationAtom);
  const applicationState = useAtomValue(getApplicationStateAtom);
  const applyJobStatusData = useAtomValue(getApplyJobStatusDataAtom);
  const listWorkerJobApplicationsHistory = useAtomValue(getListWorkerJobApplicationsHistoryDataAtom);
  const accomplishedSteps = useAtomValue(accomplishedStepsBeforeDiscardedAtom);
  const discardStatement = useDiscardStatement();
  const preInterview = usePreInterview();

  return useMemo(
    () =>
      buildApplicationTimeline({
        base,
        isSpontaneousApplication,
        applicationState,
        applyJobStatusData,
        listWorkerJobApplicationsHistory,
        accomplishedSteps,
        discardStatement,
        preInterview,
        t,
        locale,
      }),
    [
      base,
      isSpontaneousApplication,
      applicationState,
      applyJobStatusData,
      listWorkerJobApplicationsHistory,
      accomplishedSteps,
      discardStatement,
      preInterview,
      t,
      locale,
    ]
  );
};
